import React, { useState, useContext } from 'react';
import { Box, VStack, Spinner, Text, Alert, AlertIcon, AlertTitle, AlertDescription, HStack, Select, Button, Tabs, TabList, TabPanels, Tab, TabPanel, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, Table, Tbody, Tr, Td, useToast } from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { useStrictAnalysisData } from './useStrictAnalysisData';
import { callApi } from '../../callApi';
import { AppContext } from '../AppContext';
import CaseHeaderPanel from './CaseHeaderPanel';
import CarrierInfoPanel from './CarrierInfoPanel';
import TechReportMetadataPanel from './TechReportMetadataPanel';
import MedicalNecessityPanel from './MedicalNecessityPanel';
import CodingAnalysisPanel from './CodingAnalysisPanel';
import PayerResponsePanel from './PayerResponsePanel';
import DocumentManagerPanel from './DocumentManagerPanel';
import HeaderMenus from '../HeaderMenus';
import ModernChat from '../ModernChat';

const NMAStrictAnalysis = ({ caseId }) => {
  const {
    initialLoading,
    error,
    caseData,
    carrierData,
    caseFiles,
    results,
    claimsData,
    payerResponse,
    techMetadata,
    setTechMetadata,
    isTechMetadataLoading,
    claimType,
    setClaimType,
    pipelineVersion,
    setPipelineVersion,
    skipFetchRef,
    isNMA,
    isAdmin,
    refreshData
  } = useStrictAnalysisData(caseId);

  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const [localError, setLocalError] = useState(null);
  const [loadingMode, setLoadingMode] = useState(null);
  const [isRecodingCpt, setIsRecodingCpt] = useState(false);

  // Extraction States
  const [isTechMetadataExtracting, setIsTechMetadataExtracting] = useState(false);
  const [isSummaryMetadataExtracting, setIsSummaryMetadataExtracting] = useState(false);
  const [summaryMetadata, setSummaryMetadata] = useState(null);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);

  // Generation States
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [isJsonGenerating, setIsJsonGenerating] = useState(false);
  const [isCptPdfGenerating, setIsCptPdfGenerating] = useState(false);
  const [isTimelinePdfGenerating, setIsTimelinePdfGenerating] = useState(false);
  const [jsonContent, setJsonContent] = useState(null);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);

  const runAnalysis = (mode) => {
    // This would trigger the analysis API call
    // For now, we just update the state to show loading
    setLoadingMode(mode);
    setPipelineVersion(mode);
    skipFetchRef.current = true;
    // Actual API call logic would go here, similar to the original file
  };

  const handleExtractTechMetadata = async () => {
    setIsTechMetadataExtracting(true);
    setLocalError(null);
    try {
      const response = await callApi(`/nma/extract_tech_report_metadata`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ case_id: caseId }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.metadata) {
          setTechMetadata(data.metadata);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to extract metadata.');
      }
    } catch (err) {
      setLocalError(err.message || 'An error occurred while extracting metadata.');
    } finally {
      setIsTechMetadataExtracting(false);
    }
  };

  const handleExtractSummaryMetadata = async () => {
    setIsSummaryMetadataExtracting(true);
    setLocalError(null);
    try {
      const response = await callApi(`/nma/extract_summary_report_metadata`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ case_id: caseId }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.metadata) {
          setSummaryMetadata(data.metadata);
          setIsSummaryModalOpen(true);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to extract summary metadata.');
      }
    } catch (err) {
      setLocalError(err.message || 'An error occurred while extracting summary metadata.');
    } finally {
      setIsSummaryMetadataExtracting(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!caseId || !claimType) {
      setLocalError('Case ID or Claim Type is missing.');
      return;
    }

    setIsPdfGenerating(true);
    setLocalError(null);
    try {
      const response = await callApi(`/v2/cases/${caseId}/generate-report`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: 'nma_strict_cms1500_v2',
          claim_type: claimType,
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, '_blank');

        if (newTab) {
          newTab.focus();
        } else {
          setLocalError('Your browser blocked the new tab. Please allow pop-ups for this site to view the PDF.');
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate PDF.');
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setLocalError(err.message);
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handleGenerateCptPdf = async () => {
    if (!caseId) {
      setLocalError('Case ID is missing.');
      return;
    }

    setIsCptPdfGenerating(true);
    setLocalError(null);
    try {
      const response = await callApi(`/v2/cases/${caseId}/generate-report`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: 'nma_comprehensive_cpt_summary_v1',
          claim_type: claimType,
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, '_blank');

        if (newTab) {
          newTab.focus();
        } else {
          setLocalError('Your browser blocked the new tab. Please allow pop-ups for this site to view the PDF.');
        }
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate CPT PDF.');
      }
    } catch (err) {
      console.error('Error generating CPT PDF:', err);
      setLocalError(err.message);
    } finally {
      setIsCptPdfGenerating(false);
    }
  };

  const handleGenerateTimelinePdf = async () => {
    if (!caseId) {
      setLocalError('Case ID is missing.');
      return;
    }

    setIsTimelinePdfGenerating(true);
    setLocalError(null);
    try {
      const response = await callApi(`/api/internal/zinniax/cases/${caseId}/generate-timeline-report`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, '_blank');

        if (newTab) {
          newTab.focus();
        } else {
          setLocalError('Popup blocked. Please allow popups for this site to view the PDF.');
        }
      } else {
        const errorData = await response.json();
        setLocalError(errorData.error || 'Failed to generate Timeline PDF.');
      }
    } catch (err) {
      console.error('Error generating Timeline PDF:', err);
      setLocalError(err.message || 'Failed to generate Timeline PDF.');
    } finally {
      setIsTimelinePdfGenerating(false);
    }
  };

  const handleGenerateJson = async () => {
    if (!caseId || !claimType) {
      setLocalError('Case ID or Claim Type is missing.');
      return;
    }

    setIsJsonGenerating(true);
    setLocalError(null);
    try {
      const response = await callApi(`/integration/cases/${caseId}/generate-json-report`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: 'nma_strict_pro_json',
          claim_type: claimType,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setJsonContent(data);
        setJsonModalOpen(true);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate JSON report.');
      }
    } catch (err) {
      console.error('Error generating JSON report:', err);
      setLocalError(err.message);
    } finally {
      setIsJsonGenerating(false);
    }
  };

  if (initialLoading && !results) {
    return (
      <Box>
        <HeaderMenus />
        <VStack py={12} spacing={4}>
          <Spinner size="xl" color="blue.500" thickness="4px" />
          <Text>Checking for existing analysis...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      <HeaderMenus />
      <Box p={8} maxWidth="1200px" margin="0 auto">
        
        <CaseHeaderPanel 
          caseData={caseData} 
          pipelineVersion={pipelineVersion} 
          isNMA={isNMA} 
          isAdmin={isAdmin} 
        >
          <HStack spacing={4} flexWrap="wrap">
            <Select 
              value={claimType} 
              onChange={(e) => setClaimType(e.target.value)}
              width="150px"
              bg="white"
            >
              <option value="pro">Pro Claim</option>
              <option value="tech">Tech Claim</option>
            </Select>
            <Button 
              colorScheme="blue" 
              isLoading={isRecodingCpt}
              loadingText="Recoding..."
              onClick={async () => {
                setIsRecodingCpt(true);
                try {
                  const response = await callApi('/ionm_code_from_json', accessToken, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      data_type: 'json',
                      case_id: caseId,
                      no_refresh: false,
                    })
                  });
                  
                  if (response.ok) {
                    toast({
                      title: 'CPT Recode Successful',
                      description: 'The case has been recoded. Refreshing analysis...',
                      status: 'success',
                      duration: 3000,
                      isClosable: true,
                    });
                    // Re-run the analysis to fetch the fresh data
                    refreshData();
                  } else {
                    throw new Error('Failed to recode case');
                  }
                } catch (error) {
                  toast({
                    title: 'Error',
                    description: 'Failed to recode CPTs.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                  });
                } finally {
                  setIsRecodingCpt(false);
                }
              }}
            >
              Recode CPT
            </Button>
            {/* Hidden buttons: Generate Mode 2, Extract Tech Metadata, Extract Summary Metadata */}

            {results && (
              <>
                <Button
                  colorScheme="green"
                  leftIcon={<DownloadIcon />}
                  onClick={handleGeneratePdf}
                  isLoading={isPdfGenerating}
                  loadingText="Generating..."
                >
                  Generate ICD10 PDF
                </Button>
                <Button
                  colorScheme="blue"
                  leftIcon={<DownloadIcon />}
                  onClick={handleGenerateJson}
                  isLoading={isJsonGenerating}
                  loadingText="Generating..."
                >
                  Generate JSON
                </Button>
                <Button
                  colorScheme="purple"
                  leftIcon={<DownloadIcon />}
                  onClick={handleGenerateCptPdf}
                  isLoading={isCptPdfGenerating}
                  loadingText="Generating..."
                >
                  Generate CPT PDF
                </Button>
                <Button
                  colorScheme="teal"
                  leftIcon={<DownloadIcon />}
                  onClick={handleGenerateTimelinePdf}
                  isLoading={isTimelinePdfGenerating}
                  loadingText="Generating..."
                >
                  Generate Timeline PDF
                </Button>
              </>
            )}
          </HStack>
        </CaseHeaderPanel>

        {(error || localError) && (
          <Alert status="error" mb={6} borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error || localError}</AlertDescription>
            </Box>
          </Alert>
        )}

        <Tabs variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab>Strict Analysis</Tab>
            <Tab>Dx Chat</Tab>
            <Tab>CPT Chat</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0} py={4}>
              
              <CarrierInfoPanel data={carrierData} />
              
              <TechReportMetadataPanel metadata={techMetadata} isLoading={isTechMetadataLoading} />
              
              {loadingMode && !results ? (
                <VStack py={12} spacing={4}>
                  <Spinner size="xl" color="blue.500" thickness="4px" />
                  <Text>Running strict rule evaluation and scoring...</Text>
                </VStack>
              ) : (
                <>
                  <MedicalNecessityPanel results={results} />
                  <CodingAnalysisPanel results={results} claimsData={claimsData} claimType={claimType} />
                </>
              )}

              <PayerResponsePanel payerResponse={payerResponse} />
              
              <DocumentManagerPanel caseId={caseId} caseFiles={caseFiles} onUploadSuccess={refreshData} />

            </TabPanel>

            <TabPanel>
              <Box h="600px">
                <ModernChat 
                  caseId={caseId} 
                  contextType="icd10_context" 
                  height="600px"
                  additionalContext={results ? JSON.stringify(results, null, 2) : null}
                />
              </Box>
            </TabPanel>

            <TabPanel>
              <Box h="600px">
                <ModernChat 
                  caseId={caseId} 
                  contextType="cpt_ionm_context" 
                  height="600px"
                  additionalContext={results ? JSON.stringify(results, null, 2) : null}
                />
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>

      </Box>

      {/* Summary Report Metadata Modal */}
      <Modal isOpen={isSummaryModalOpen} onClose={() => setIsSummaryModalOpen(false)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Summary Report Metadata</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {summaryMetadata ? (
              <Table variant="simple" size="sm">
                <Tbody>
                  {Object.entries(summaryMetadata).map(([key, value]) => (
                    <Tr key={key}>
                      <Td fontWeight="bold" textTransform="capitalize" width="40%">
                        {key.replace(/_/g, ' ')}
                      </Td>
                      <Td>{value}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            ) : (
              <Text>No metadata found.</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={() => setIsSummaryModalOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* JSON Report Modal */}
      <Modal isOpen={jsonModalOpen} onClose={() => setJsonModalOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>JSON Report Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box 
              p={4} 
              borderWidth="1px" 
              borderRadius="md" 
              bg="gray.50"
              maxHeight="70vh" 
              overflowY="auto"
              fontSize="sm"
              fontFamily="monospace"
              whiteSpace="pre-wrap"
            >
              <pre>{JSON.stringify(jsonContent, null, 2)}</pre>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button 
              colorScheme="green" 
              mr={3} 
              leftIcon={<DownloadIcon />}
              onClick={() => {
                const jsonString = JSON.stringify(jsonContent, null, 2);
                const blob = new Blob([jsonString], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = `strict_medical_necessity_report_${claimType}_${caseId}.json`;
                document.body.appendChild(a);
                a.click();
                setTimeout(() => {
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                }, 100);
              }}
            >
              Download JSON
            </Button>
            <Button variant="ghost" onClick={() => setJsonModalOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default NMAStrictAnalysis;
